import { useOTSTranslation } from 'i18n/useOTSTranslation';
import React, { useCallback } from 'react';
import { Routes } from 'routes';
import classNames from 'classnames';

import { ISubLink } from 'components/common/StickyTabsNav/types';
import { TabLink } from 'components/blocks/TabNav/Link';
import { useAnalytics, SubscriberDataEvent } from 'util/analytics';
import Link from 'next/link';
import { IResortPathInfo } from 'api/resort/types';
import { PlayerYouTube } from 'components/home/PlayerYouTube';
import { ReactComponent as SvgD } from 'public/assets/icons/swallowtail-flag.svg';

import { IRegionShort, IRelatedForRegion, IRelatedForResort } from 'api/region/types';
import styles from './styles.module.scss';
import { GlobalHeroNav } from './GlobalHeroNav';

const PILLS_TO_SHOW = 2;

/**
 * Renders resort/region navigation with background hero image.
 */
export const HeroNav = ({
  links,
  link,
  isPartner,
  website,
  phone,
  resort,
  video,
  title,
  region,
  related,
  backgroundPhoto,
}: {
  title: string,
  backgroundPhoto: string,
  links?: ISubLink<string>[],
  link?: string,
  isPartner?: boolean,
  website?: string,
  phone?: string,
  resort?: IResortPathInfo,
  video?: string,
  region?:IRegionShort,
  related?: IRelatedForRegion | IRelatedForResort,
}) => {
  const { t } = useOTSTranslation('common');
  const a = useAnalytics();

  const onWebsiteClick = useCallback(() => {
    a.subcriberEvent(SubscriberDataEvent.HomePageClick, { url: website });
  }, [a, website]);

  const onPhoneClick = useCallback(() => {
    a.subcriberEvent(SubscriberDataEvent.PhoneClick, { url: `tel:${phone}` });
  }, [a, phone]);

  const isResortPage = related && 'nearby' in related;

  if (!resort && !region) {
    return <GlobalHeroNav title={title} backgroundPhoto={backgroundPhoto} />;
  }

  return (
    <div
      className={classNames(styles.header)}
    >
      <div className={classNames(styles.content)}>
        <div className={styles.hero}>
          {isPartner && (
          <div className={classNames(styles.topContainer)}>

            <div className={classNames(styles.videoContainer, !video && styles.noVideo)}>
              <div className={classNames(styles.premierPartnerBadge, !video && styles.noVideoBadge)}>
                <div className={classNames(styles.background, !video && styles.noVideoBadge)}>
                  <SvgD width={video ? 230 : 150} height={32} />

                </div>
                {t('common.premierPartner').toUpperCase()}
                <div className={classNames(styles.premierPartnerLinks)}>
                  {website && (
                  <a
                    onClick={onWebsiteClick}
                    href={website}
                    target="_blank"
                    rel="noreferrer nofollow"
                  >
                    {t('common.website')}
                  </a>
                  )}

                  {phone && (
                  <a
                    onClick={onPhoneClick}
                    href={`tel:${phone}`}
                    target="_blank"
                    rel="noreferrer nofollow"
                  >
                    {phone}
                  </a>
                  )}
                </div>
              </div>

              <div className={styles.videoPreview}>
                {video && (
                  <PlayerYouTube
                    urlYouTube={video}
                  />
                )}
              </div>
            </div>
          </div>
          )}
          {region && resort && (
          <div className={styles.topContent}>
            <div className={classNames(styles.title, isPartner && styles.partnerOffset)}>
              <h1>{title}</h1>
            </div>
            {isResortPage && (
            <div className={classNames(styles.subtitle)}>
              <h2>
                <Link href={Routes.regionSnowReport(region)} prefetch={false}>{region.title.toUpperCase()}</Link>
                {' '}
                <span className={styles.seperator}>/</span>
                {' '}
                {resort.title
                  .toUpperCase()}

              </h2>
            </div>
            )}
            {isResortPage && (
            <div className={styles.tagsContainer}>
              <div className={styles.rows}>
                <div className={styles.row}>
                  {related.related?.length && related.related?.length > 0 ? (
                    <p>
                      {t('common.relatedRegions')}
                    </p>
                  ) : null}
                  {related.related?.slice(0, PILLS_TO_SHOW)
                    .map((item) => <Link key={item.uuid} href={Routes.regionSnowReport(item)} className={styles.tag}>{item.title}</Link>)}
                </div>
                {(related as IRelatedForResort).nearby && (
                <div className={styles.row}>
                  <p>{t('common.nearbyResorts')}</p>
                  {(related as IRelatedForResort)
                    .nearby
                    ?.map((item) => (
                      <Link key={item.uuid} href={Routes.resortSnowReport(item)} className={styles.tag}>
                        {item.title_short}
                      </Link>
                    ))
                    .slice(0, PILLS_TO_SHOW)}
                </div>
                )}
              </div>
            </div>
            )}
          </div>
          )}
        </div>

        { links && (
        <div className={classNames(styles.links)}>
          {links.map((tI) => (

            <TabLink
              href={tI.href}
              current={tI.uuid === link}
              external={!!tI.isExternal}
              key={tI.uuid}
              isRedesigned
            >
              {tI.title}
            </TabLink>
          ))}

        </div>
        )}
      </div>
    </div>
  );
};
